// File: src/pages/CitizenReportNew.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  AlertCircle, 
  FileText, 
  Upload,
  User,
  Home,
  X,
  Image,
  File,
  Loader2
} from 'lucide-react';
import { apiService, API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const CitizenReportNew: React.FC = () => {
  const navigate = useNavigate();
  const { citizen, token, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    wardId: '',
    constituencyId: '',
    urgencyScore: 5,
    isAnonymous: false,
  });
  
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<string[]>([]);

  useEffect(() => {
    if (!token || !citizen) {
      toast.error('Please login to continue');
      navigate('/login');
    }
  }, [token, citizen, navigate]);

  // Complete category list matching backend IssueCategory enum exactly
  const categories = [
    { value: 'WATER_AND_SANITATION', label: ' Water & Sanitation', group: 'Infrastructure' },
    { value: 'WATER_SCARCITY', label: ' Water Scarcity', group: 'Infrastructure' },
    { value: 'ROADS_AND_TRANSPORT', label: ' Roads & Transport', group: 'Infrastructure' },
    { value: 'ROADS_DRAINAGE', label: ' Roads & Drainage', group: 'Infrastructure' },
    { value: 'DRAINAGE', label: ' Drainage', group: 'Infrastructure' },
    { value: 'ELECTRICITY', label: ' Electricity', group: 'Infrastructure' },
    { value: 'HEALTHCARE', label: ' Healthcare', group: 'Social Services' },
    { value: 'EDUCATION', label: ' Education', group: 'Social Services' },
    { value: 'SECURITY', label: ' Security', group: 'Safety' },
    { value: 'WASTE_MANAGEMENT', label: ' Waste Management', group: 'Environment' },
    { value: 'FLOODING', label: ' Flooding', group: 'Environment' },
    { value: 'YOUTH_EMPLOYMENT', label: ' Youth Employment', group: 'Economic' },
    { value: 'LAND_DISPUTES', label: ' Land Disputes', group: 'Legal' },
    { value: 'LIVESTOCK_DISEASES', label: ' Livestock Diseases', group: 'Agriculture' },
    { value: 'HELB_LOANS', label: ' HELB Loans', group: 'Education' },
    { value: 'CORRUPTION', label: ' Corruption', group: 'Governance' },
    { value: 'OTHER', label: ' Other', group: 'General' },
  ];

  // Group categories for better organization
  const groupedCategories = categories.reduce((acc, category) => {
    if (!acc[category.group]) {
      acc[category.group] = [];
    }
    acc[category.group].push(category);
    return acc;
  }, {} as Record<string, typeof categories>);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUrgencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setFormData(prev => ({ ...prev, urgencyScore: value }));
    if (value >= 8) {
      toast.success(`High urgency level (${value}/10) - This report will be prioritized`, { duration: 2000 });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const validFiles = filesArray.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Max size is 10MB`);
          return false;
        }
        return true;
      });
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (reportId: string): Promise<boolean> => {
    if (files.length === 0) return true;
    
    setUploadingFiles(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload attachments');
      }
      
      const data = await response.json();
      if (data.success) {
        setUploadedAttachments(data.data.attachments);
        toast.success(`${files.length} attachment(s) uploaded successfully`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload some attachments');
      return false;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please provide a description');
      return;
    }
    
    if (formData.urgencyScore < 1 || formData.urgencyScore > 10) {
      toast.error('Urgency score must be between 1 and 10');
      return;
    }
    
    if (!citizen) {
      toast.error('Please login again');
      navigate('/login');
      return;
    }

    setLoading(true);
    
    try {
      refreshAuth();
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      const reportData = {
        category: formData.category,
        description: formData.description,
        wardId: formData.wardId ? parseInt(formData.wardId) : (citizen.wardId || null),
        constituencyId: formData.constituencyId ? parseInt(formData.constituencyId) : (citizen.constituencyId || null),
        urgencyScore: formData.urgencyScore,
        isAnonymous: formData.isAnonymous,
        phoneNumber: citizen.phoneNumber,
        fullname: formData.isAnonymous ? undefined : citizen.fullName,
      };

      console.log('📝 Submitting report with category:', formData.category);
      console.log('📝 Full report data:', reportData);
      
      const response = await apiService.createReport(reportData);
      
      if (response.data.success) {
        const report = response.data.data;
        
        if (files.length > 0 && report && report.id) {
          await uploadFiles(report.id);
        }
        
        toast.success(`Report submitted successfully! Urgency: ${formData.urgencyScore}/10`);
        navigate('/citizen/dashboard');
      } else {
        throw new Error(response.data.error || 'Failed to submit report');
      }
    } catch (error: any) {
      console.error('❌ Error submitting report:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        apiService.clearAuth();
        navigate('/login');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!citizen) {
    return (
      <div className="min-h-screen bg-[#f4f7f6] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b2045] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const getUrgencyColor = (score: number) => {
    if (score >= 8) return 'text-red-600 bg-red-100';
    if (score >= 5) return 'text-[#ca8a04] bg-[#fefce8]';
    return 'text-[#3c8d2e] bg-[#3c8d2e]/10';
  };

  const getUrgencyLabel = (score: number) => {
    if (score >= 8) return 'High Priority';
    if (score >= 5) return 'Medium Priority';
    return 'Low Priority';
  };

  return (
    <div className="min-h-screen bg-[#f4f7f6]">
      <button
        onClick={() => navigate('/')}
        className="fixed top-4 left-4 z-10 flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm text-[#0b2045] rounded-lg hover:bg-white hover:shadow-md transition-all duration-200"
      >
        <Home size={18} className="mr-2" />
        Back to Home
      </button>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/citizen/dashboard')}
            className="flex items-center text-gray-600 hover:text-[#3c8d2e] mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-[#0b2045]">Submit New Report</h1>
          <p className="text-gray-600">Report a community issue that needs attention</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-[#0b2045]/10">
          <div className="flex items-start">
            <div className="bg-[#3c8d2e]/10 p-2 rounded-lg mr-4">
              <User className="text-[#3c8d2e]" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-[#0b2045]">Reporting as</h3>
              {formData.isAnonymous ? (
                <p className="text-gray-600">Anonymous Citizen</p>
              ) : (
                <div>
                  <p className="text-[#0b2045] font-medium">{citizen.fullName}</p>
                  <p className="text-sm text-gray-600">{citizen.phoneNumber}</p>
                  {citizen.wardName && (
                    <p className="text-xs text-gray-500 mt-1">Ward: {citizen.wardName}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isAnonymous"
                name="isAnonymous"
                checked={formData.isAnonymous}
                onChange={handleInputChange}
                className="h-4 w-4 text-[#3c8d2e] rounded focus:ring-[#3c8d2e]"
              />
              <label htmlFor="isAnonymous" className="ml-2 text-sm text-gray-700">
                Submit anonymously
              </label>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
            <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Issue Details</h2>
            
            {/* Category - Enhanced with optgroups */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <AlertCircle className="inline mr-2" size={16} />
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
              >
                <option value="">Select a category</option>
                {Object.entries(groupedCategories).map(([group, groupCategories]) => (
                  <optgroup key={group} label={group}>
                    {groupCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the category that best describes your issue
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline mr-2" size={16} />
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder="Describe the issue in detail. What needs to be fixed? How is it affecting the community?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ward ID (Optional - defaults to your ward)
                </label>
                <input
                  type="number"
                  name="wardId"
                  value={formData.wardId}
                  onChange={handleInputChange}
                  placeholder={citizen.wardId?.toString() || "e.g., 123"}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                />
                {citizen.wardName && (
                  <p className="text-xs text-gray-500 mt-1">Your ward: {citizen.wardName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Constituency ID (Optional)
                </label>
                <input
                  type="number"
                  name="constituencyId"
                  value={formData.constituencyId}
                  onChange={handleInputChange}
                  placeholder={citizen.constituencyId?.toString() || "e.g., 456"}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                />
                {citizen.constituencyName && (
                  <p className="text-xs text-gray-500 mt-1">Your constituency: {citizen.constituencyName}</p>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Urgency Level *
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Low (1)</span>
                  <input
                    type="range"
                    name="urgencyScore"
                    min="1"
                    max="10"
                    step="1"
                    value={formData.urgencyScore}
                    onChange={handleUrgencyChange}
                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3c8d2e 0%, #ca8a04 ${(formData.urgencyScore - 1) * 11.11}%, #e5e7eb ${(formData.urgencyScore - 1) * 11.11}%, #e5e7eb 100%)`
                    }}
                  />
                  <span className="text-sm text-gray-600">High (10)</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className={`px-3 py-1 rounded-lg text-sm font-medium ${getUrgencyColor(formData.urgencyScore)}`}>
                    {getUrgencyLabel(formData.urgencyScore)}: {formData.urgencyScore}/10
                  </div>
                  {formData.urgencyScore >= 8 && (
                    <div className="text-xs text-red-600 flex items-center">
                      <AlertCircle size={12} className="mr-1" />
                      This will be marked as urgent
                    </div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Rate how urgent this issue is (1 = Low priority, 10 = Critical/Emergency)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Upload className="inline mr-2" size={16} />
                Attachments (Optional)
              </label>
              
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                files.length > 0 ? 'border-[#3c8d2e] bg-[#e8f1ec]' : 'border-gray-300 hover:border-[#0b2045]'
              }`}>
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                  disabled={uploadingFiles}
                />
                <label
                  htmlFor="file-upload"
                  className={`cursor-pointer flex flex-col items-center ${uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Upload className={`mb-2 ${files.length > 0 ? 'text-[#3c8d2e]' : 'text-gray-400'}`} size={32} />
                  <span className={`text-sm ${files.length > 0 ? 'text-[#3c8d2e] font-medium' : 'text-gray-600'}`}>
                    {files.length > 0 ? 'Add more files' : 'Click to upload photos or documents'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Supports JPG, PNG, PDF, DOC (Max 10MB each)
                  </span>
                </label>
              </div>
              
              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-[#0b2045]">
                      Selected files ({files.length})
                    </span>
                    {uploadingFiles && (
                      <span className="text-xs text-[#3c8d2e] flex items-center">
                        <Loader2 size={12} className="animate-spin mr-1" />
                        Uploading...
                      </span>
                    )}
                  </div>
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-[#f4f7f6] p-3 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {file.type.startsWith('image/') ? (
                          <Image size={20} className="text-[#3c8d2e]" />
                        ) : (
                          <File size={20} className="text-gray-500" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-[#0b2045]">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                        disabled={uploadingFiles}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-gray-400 mt-2">
                Attachments help authorities understand the issue better. Photos and documents are securely stored.
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center pt-6">
            <button
              type="button"
              onClick={() => navigate('/citizen/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-[#f4f7f6] font-medium transition-colors"
              disabled={loading || uploadingFiles}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingFiles}
              className="px-6 py-3 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
            >
              {(loading || uploadingFiles) ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {uploadingFiles ? 'Uploading...' : 'Submitting...'}
                </>
              ) : (
                'Submit Report'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CitizenReportNew;